import { getEndpoints, getConfig, getSupabaseClient } from './config.js';
import { isAuthenticated, getAuthToken } from './auth.js';
import { post } from './api.js';

/**
 * Lightswitch SDK Core Entitlement
 * 
 * Handles feature entitlement checks and related functionality.
 * Pure JavaScript implementation with no framework dependencies.
 */

/**
 * Make a request for entitlement check with optional authentication
 * @param {string} url - The URL to make the request to
 * @param {Object} data - Data to send in the request body
 * @returns {Promise<any>} Parsed JSON response
 */
const fetchForEntitlement = async (url, data) => {
  try {
    const config = getConfig();
    const headers = {
      'X-Publishable-Key': config.publishableKey,
      'X-App-Slug': config.appSlug || 'default',
      'Content-Type': 'application/json'
    };

    // Try to get Supabase token first, then fallback to Lightswitch token
    let authToken = null;
    
    // Try Supabase token
    try {
      const supabase = getSupabaseClient();
      if (supabase) {
        const { data: { session }, error } = await supabase.auth.getSession();
        if (!error && session?.access_token) {
          authToken = session.access_token;
          console.log('Using Supabase token for entitlement check');
        }
      }
    } catch (error) {
      console.log('Could not get Supabase token:', error.message);
    }
    
    // Fallback to Lightswitch token if no Supabase token
    if (!authToken && isAuthenticated()) {
      authToken = getAuthToken();
      console.log('Using Lightswitch token for entitlement check');
    }

    // Add auth header if we have a token
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    } else {
      console.log('No authentication token available for entitlement check');
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Entitlement check request error:', error);
    throw error;
  }
};


/**
 * Check if user is entitled to use a specific feature or general access
 * @param {string} [feature] - Feature identifier to check (optional, defaults to 'has_access')
 * @param {Object} [options={}] - Options for the entitlement check
 * @returns {Promise<Object>} Entitlement result object
 */
const checkEntitlement = async function (feature) {
  try {
    const endpoints = getEndpoints();
    
    // Prepare request body - only include feature if specified
    const requestBody = {};
    if (feature) {
      requestBody.feature = feature;
    }
    
    const response = await fetchForEntitlement(endpoints.entitlement_check, requestBody);
    
    const featureKey = feature || 'has_access';
    console.log(`Entitlement check for ${featureKey}:`, response);
    console.log(`Paywall config ID from API:`, response?.paywall_config_id);
    
    const result = {
      entitled: response?.entitled === true,
      feature: featureKey,
      paywallConfigId: response?.paywall_config_id,
      error: null,
      ...response
    };
    console.log(`Final entitlement result:`, result);
    return result;
  } catch (error) {
    console.error('Error checking entitlement:', error);
    const errorResult = {
      entitled: false,
      feature: feature || 'has_access',
      error: error.message || 'Failed to check entitlement'
    };
    return errorResult;
  }
};

export { checkEntitlement };
//# sourceMappingURL=entitlement.js.map
